import type { FetchResult, Reference, StoreObject } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useIntl } from 'react-intl';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSnackbar } from '@hooks/useSnackbar';

import {
  type GetAlertRecipientSettingQuery,
  type UpdateAlertRecipientSettingMutationVariables,
  type UpdateAlertRecipientSettingMutation,
  type VerifyAlertCodeMutationVariables,
  type VerifyAlertCodeMutation,
  GetAlertRecipientSettingDocument,
  useGetAlertRecipientSettingQuery,
  useUpdateAlertRecipientSettingMutation,
  useVerifyAlertCodeMutation,
} from 'generated/alert_recipient_setting.generated';
import type { AlertRecipientType } from 'generated/types';

export const useGetAlertRecipientSetting = (
  type: AlertRecipientType,
  id: string
) => {
  // Don't use useResponseHandler - may not have recipient settings
  const response = useGetAlertRecipientSettingQuery({
    variables: {
      input: {
        ...(type === 'user' && {
          user_id: id,
        }),
        ...(type === 'account' && {
          account_id: id,
        }),
      },
    },
  });
  const data = response.data?.getAlertRecipientSetting;

  return { ...response, data };
};

export function useUpsertAlertRecipientSetting(): [
  (
    params: UpdateAlertRecipientSettingMutationVariables['input']
  ) => Promise<FetchResult<UpdateAlertRecipientSettingMutation>>,
  boolean,
] {
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateAlertRecipientSetting',
  });
  const [mutate, { loading: baseLoading }] =
    useUpdateAlertRecipientSettingMutation();
  const loading = baseLoading;

  async function upsertAlertRecipientSetting(
    params: UpdateAlertRecipientSettingMutationVariables['input']
  ) {
    const response = await mutate({
      variables: { input: { ...params } },
      update: (cache, { data: response }) => {
        if (!response?.updateAlertRecipientSetting) {
          return;
        }
        const { account_id, user_id } = response.updateAlertRecipientSetting;
        const normalizedId = cache.identify({
          __typename: 'AlertRecipientSettingModel',
          user_id,
          account_id,
        });
        if (!normalizedId) {
          const storeObject =
            response.updateAlertRecipientSetting as unknown as StoreObject;
          cache.modify<{ listAlertRecipientSettings: Reference[] }>({
            fields: {
              listAlertRecipientSettings: (existing, { toReference }) => {
                const ref = toReference(storeObject);
                return ref ? [...existing, ref] : existing;
              },
            },
          });
        }
      },
    });
    const { errors } = response;
    if (errors) {
      handleError(errors);
    }
    return response;
  }
  return [upsertAlertRecipientSetting, loading];
}

export function useVerifyAlertCode(): [
  (
    params: VerifyAlertCodeMutationVariables['input']
  ) => Promise<FetchResult<VerifyAlertCodeMutation>>,
  boolean,
] {
  const client = useApolloClient();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [mutate, { loading: baseLoading }] = useVerifyAlertCodeMutation();
  const loading = baseLoading;

  async function verifyAlertCode(
    params: VerifyAlertCodeMutationVariables['input']
  ) {
    const cachedRecipientSetting =
      client.cache.readQuery<GetAlertRecipientSettingQuery>({
        query: GetAlertRecipientSettingDocument,
        variables: {
          input: {
            ...(params.user_id && {
              user_id: params.user_id,
            }),
            ...(params.account_id && {
              account_id: params.account_id,
            }),
          },
        },
      });
    const cachedSetting = cachedRecipientSetting?.getAlertRecipientSetting;
    const statuses = [cachedSetting?.email_status, cachedSetting?.sms_status];
    const refetch = !cachedSetting || statuses.every((status) => !status);

    const response = await mutate({
      variables: { input: { ...params } },
      refetchQueries: [
        ...(refetch
          ? [
              {
                query: GetAlertRecipientSettingDocument,
                variables: {
                  input: {
                    ...(params.user_id && {
                      user_id: params.user_id,
                    }),
                    ...(params.account_id && {
                      account_id: params.account_id,
                    }),
                  },
                },
              },
            ]
          : []),
      ],
      update: (cache, { data: response }) => {
        if (!response?.verifyAlertCode) {
          return;
        }
        const { email_status, sms_status } = response.verifyAlertCode;
        cache.modify({
          id: cache.identify({
            user_id: params.user_id ?? null,
            account_id: params.account_id ?? null,
            __typename: 'AlertRecipientSettingModel',
          }),
          fields: {
            email_status() {
              return email_status;
            },
            sms_status() {
              return sms_status;
            },
          },
        });
      },
    });
    const { errors } = response;
    const hasError = (errors?.length ?? 0) > 0;
    enqueueSnackbar({
      message: hasError
        ? errors?.map(({ message }) => message).join(', ')
        : formatMessage({ id: 'successMessage.verifyAlertCode' }),
      variant: hasError ? 'error' : 'success',
    });

    return response;
  }
  return [verifyAlertCode, loading];
}
